import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $nodesOfType } from 'lexical';
import { SectionAnchorNode } from '../../nodes/SectionAnchorNode';
import { Delete, Edit, Check, Close } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

function FloatingSectionAnchorToolbar({ editor, anchorElem }) {
  const toolbarRef = useRef(null);
  const inputRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [sectionId, setSectionId] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedId, setEditedId] = useState('');
  // Keep the last hovered span so we can re-position after edit mode width changes
  const hoveredSpanRef = useRef(null);
  const hideTimer = useRef(null);

  const scheduleHide = useCallback(() => {
    hideTimer.current = setTimeout(() => {
      setVisible(false);
      setEditMode(false);
    }, 200);
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  // Imperatively position the toolbar (same technique as FloatingLinkEditorPlugin)
  const reposition = useCallback(() => {
    const toolbar = toolbarRef.current;
    const span = hoveredSpanRef.current;
    if (!toolbar || !span) return;

    const spanRect = span.getBoundingClientRect();
    const anchorRect = anchorElem.getBoundingClientRect();
    const toolbarHeight = toolbar.getBoundingClientRect().height;
    const toolbarWidth = toolbar.getBoundingClientRect().width;

    let top = spanRect.top - anchorRect.top - toolbarHeight - 8;
    if (top < 0) {
      top = spanRect.top - anchorRect.top + spanRect.height + 8;
      anchorElem.setAttribute('data-anchor-popup-side', 'below');
    } else {
      anchorElem.setAttribute('data-anchor-popup-side', 'above');
    }
    let left = spanRect.left - anchorRect.left + spanRect.width / 2 - toolbarWidth / 2;
    if (left < 4) left = 4;

    toolbar.style.transform = `translate(${left}px, ${top}px)`;
    toolbar.style.opacity = '1';
  }, [anchorElem]);

  // Show toolbar whenever visible state is set (also re-runs when editMode changes to reposition)
  useEffect(() => {
    if (visible) {
      requestAnimationFrame(reposition);
    } else {
      const toolbar = toolbarRef.current;
      if (toolbar) {
        toolbar.style.opacity = '0';
        toolbar.style.transform = 'translate(-10000px, -10000px)';
        anchorElem.removeAttribute('data-anchor-popup-side');
      }
    }
  }, [visible, editMode, reposition]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (editMode && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editMode]);

  useEffect(() => {
    const editorRoot = editor.getRootElement();
    if (!editorRoot) return;

    const onMouseOver = (e) => {
      let target = e.target;
      while (target && target !== editorRoot) {
        if (target.getAttribute && target.getAttribute('data-section-anchor') === 'true') {
          cancelHide();
          hoveredSpanRef.current = target;
          setSectionId(target.id || '');
          setVisible(true);
          return;
        }
        target = target.parentElement;
      }
    };

    const onMouseOut = (e) => {
      let target = e.target;
      while (target && target !== editorRoot) {
        if (target.getAttribute && target.getAttribute('data-section-anchor') === 'true') {
          const relatedTarget = e.relatedTarget;
          if (!toolbarRef.current || !toolbarRef.current.contains(relatedTarget)) {
            scheduleHide();
          }
          return;
        }
        target = target.parentElement;
      }
    };

    editorRoot.addEventListener('mouseover', onMouseOver);
    editorRoot.addEventListener('mouseout', onMouseOut);
    return () => {
      editorRoot.removeEventListener('mouseover', onMouseOver);
      editorRoot.removeEventListener('mouseout', onMouseOut);
    };
  }, [editor, cancelHide, scheduleHide]);

  const handleDelete = () => {
    setVisible(false);
    setEditMode(false);
    editor.update(() => {
      const nodes = $nodesOfType(SectionAnchorNode);
      for (const node of nodes) {
        if (node.getSectionId() === sectionId) {
          const children = node.getChildren();
          for (const child of children) {
            node.insertBefore(child);
          }
          node.remove();
          break;
        }
      }
    });
  };

  const handleEditConfirm = () => {
    const trimmed = editedId.trim();
    if (!trimmed || !/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(trimmed)) return;
    editor.update(() => {
      const nodes = $nodesOfType(SectionAnchorNode);
      for (const node of nodes) {
        if (node.getSectionId() === sectionId) {
          node.setSectionId(trimmed);
          // Also update the live DOM span if still referenced
          if (hoveredSpanRef.current) {
            hoveredSpanRef.current.id = trimmed;
          }
          break;
        }
      }
    });
    setSectionId(trimmed);
    setEditMode(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditConfirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditMode(false);
    }
  };

  return (
    // Use the same `link-editor` CSS class as FloatingLinkEditorPlugin for visual consistency.
    // Positioning and visibility are controlled imperatively (style.transform / style.opacity).
    <div
      ref={toolbarRef}
      className="link-editor"
      onMouseEnter={cancelHide}
      onMouseLeave={scheduleHide}
    >
      {editMode ? (
        <>
          <input
            ref={inputRef}
            className="link-input"
            style={{ width: 160 }}
            value={editedId}
            placeholder="e.g. keyTakeaways"
            onChange={(e) =>
              setEditedId(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))
            }
            onKeyDown={handleKeyDown}
          />
          <div style={{ display: 'flex' }}>
            <Tooltip title="Confirm" arrow placement="top">
              <IconButton
                size="small"
                sx={{ color: '#232536', '&:hover': { color: '#3a3e5a' } }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleEditConfirm}
              >
                <Check fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel" arrow placement="top">
              <IconButton
                size="small"
                sx={{ color: '#3a3e5a' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setEditMode(false)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </>
      ) : (
        <div className="link-view">
          <span style={{ fontSize: 14, color: '#232536', fontFamily: 'monospace' }}>
            {sectionId}
          </span>
          <Tooltip title="Edit anchor ID" arrow placement="top">
            <IconButton
              size="small"
              sx={{ color: '#232536', '&:hover': { color: '#3a3e5a' } }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setEditedId(sectionId);
                setEditMode(true);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove anchor ID" arrow placement="top">
            <IconButton
              size="small"
              color="error"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleDelete}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default function FloatingSectionAnchorPlugin({ anchorElem = document.body }) {
  const [editor] = useLexicalComposerContext();
  return createPortal(
    <FloatingSectionAnchorToolbar editor={editor} anchorElem={anchorElem} />,
    anchorElem
  );
}
